import React, { useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Button, Typography, Grid } from "@material-ui/core";
import { getLocalizedString } from "@icarius-localization/strings";
import EventIcon from "@material-ui/icons/Event";
import PlaceIcon from "@material-ui/icons/Place";
import LinkIcon from "@material-ui/icons/Link";
import { openSnackbarAction } from "@icarius-common/snackbar/actions";
import { getFullDateDayName } from "@icarius-utils/date";
import { getAppColor } from "@icarius-app/selectors";
import {
    updateActivityAttemptFromSpecificTrainingAction,
    getResolutionActivityFromSpecificTrainingAction,
} from "@icarius-pages/myTraining/actions";
import { getResolutionData } from "@icarius-pages/myTraining/selectors";
import Container from "../../../container";
import UpdateSessionAttemptDialog from "./updateSessionAttemptDialog";
import MarkActivityAssistanceDialog from "./markActivityAssistanceDialog";

const SessionsContent = (props) => {
    const { data, trainingData, activityData } = props;

    const [selectedSessionIndexToUpdate, setSelectedSessionIndexToUpdate] = useState(null);
    const [markAssistanceDialogIsOpen, setMarkAssistanceDialogIsOpen] = useState(false);

    const dispatch = useDispatch();
    const color = useSelector(getAppColor);
    const resolutionData = useSelector(getResolutionData);
    const readOnly = !trainingData.isInstructor || trainingData.isFinished;

    const handleEditClick = (session, sessionIndex) => {
        if (!session.canUpdate) {
            dispatch(openSnackbarAction({ msg: getLocalizedString("cannotEditSession"), severity: "warning" }));
            return;
        }
        setSelectedSessionIndexToUpdate(sessionIndex);
    };

    const handleSubmitUpdate = (dataToSend) => {
        dispatch(
            updateActivityAttemptFromSpecificTrainingAction(
                trainingData.id,
                activityData.id,
                selectedSessionIndexToUpdate,
                dataToSend
            )
        );
    };

    const handleOpenMarkActivityAssistanceDialog = (sessionIndex) => {
        dispatch(getResolutionActivityFromSpecificTrainingAction(trainingData.id, activityData.id, sessionIndex)).then(
            (resp) => {
                if (resp.status === 200) {
                    setMarkAssistanceDialogIsOpen(true);
                }
            }
        );
    };

    if (!data?.length) return null;

    return (
        <>
            {data.map((session, sessionIndex) => {
                const sessionHasData = Boolean(
                    (session.date && session.time) || session.location || session.additionalInfo
                );
                return (
                    <Container
                        key={sessionIndex}
                        title={`${getLocalizedString("session")} ${sessionIndex + 1} (${getLocalizedString(
                            session.type === "V" ? "virtual" : "inPerson"
                        )})`}
                    >
                        <div
                            style={{
                                display: "flex",
                                flexDirection: "column",
                                gap: 10,
                                fontSize: 14,
                            }}
                        >
                            {!sessionHasData && (
                                <Typography>
                                    {getLocalizedString(
                                        !trainingData.isParticipant ? "noSessionDataShort" : "noSessionData"
                                    )}
                                </Typography>
                            )}
                            {Boolean(session.date && session.time) && (
                                <Typography
                                    style={{
                                        fontSize: 14,
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 5,
                                    }}
                                >
                                    <EventIcon style={{ height: 20 }} /> {getFullDateDayName(session.date)} -{" "}
                                    {session.time} {getLocalizedString("hrs")}
                                </Typography>
                            )}
                            {Boolean(session.type === "V" && session.location) && (
                                <Typography
                                    style={{
                                        fontSize: "inherit",
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 5,
                                    }}
                                >
                                    <LinkIcon style={{ height: 20 }} />
                                    <a
                                        className="whiteText"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        href={session.location}
                                    >
                                        {getLocalizedString("accessSession")}
                                    </a>
                                </Typography>
                            )}
                            {Boolean(session.type !== "V" && session.location) && (
                                <Typography
                                    style={{
                                        fontSize: "inherit",
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 5,
                                    }}
                                >
                                    <PlaceIcon style={{ height: 20 }} />
                                    {session.location}
                                </Typography>
                            )}
                            {session.additionalInfo && (
                                <Typography variant="caption" style={{ fontSize: "inherit" }}>
                                    {session.additionalInfo}
                                </Typography>
                            )}
                            {!trainingData.isParticipant && !trainingData.isFinished && (
                                <Grid container item xs={12} spacing={1}>
                                    {!readOnly && (
                                        <Grid container item xs={12} sm={6}>
                                            <Button
                                                onClick={() => handleEditClick(session, sessionIndex)}
                                                variant="contained"
                                                className="whiteText"
                                                fullWidth
                                            >
                                                {getLocalizedString("edit")}
                                            </Button>
                                        </Grid>
                                    )}
                                    <Grid container item xs={12} sm={readOnly ? 12 : 6}>
                                        <Button
                                            onClick={() => handleOpenMarkActivityAssistanceDialog(sessionIndex)}
                                            variant="contained"
                                            className="whiteText"
                                            style={{ background: color, border: 0 }}
                                            fullWidth
                                        >
                                            {getLocalizedString(readOnly ? "viewAssistance" : "registerAssistance")}
                                        </Button>
                                    </Grid>
                                </Grid>
                            )}
                        </div>
                    </Container>
                );
            })}
            {selectedSessionIndexToUpdate !== null && (
                <UpdateSessionAttemptDialog
                    open={selectedSessionIndexToUpdate !== null}
                    data={data[selectedSessionIndexToUpdate]}
                    isSession={true}
                    handleSubmit={handleSubmitUpdate}
                    handleClose={() => setSelectedSessionIndexToUpdate(null)}
                />
            )}
            {markAssistanceDialogIsOpen && (
                <MarkActivityAssistanceDialog
                    open={markAssistanceDialogIsOpen}
                    readOnly={readOnly}
                    trainingData={trainingData}
                    activityData={activityData}
                    resolution={resolutionData}
                    handleClose={() => setMarkAssistanceDialogIsOpen(false)}
                />
            )}
        </>
    );
};

export default SessionsContent;
